import * as React from 'react';

import { Button, Dropdown, Grid, Input, Message, TextArea } from 'semantic-ui-react';

import { localeData } from '../../locales/data';
import { convertArrayFromDropdownProperties } from '../../shared/helpers';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { getIsPowerPoolAlpha, POWER_POOL_DESCRIPTION_TEMPLATE } from '../../shared/helpers/AlphaPropertiesValidation';
import ColorProperties from '../ColorProperties/ColorProperties';
import { FieldSet } from '../Forms';

import { AlphaPropertiesDescriptionProps, AlphaPropertiesPropsMain } from './types';
import { AlphaIdCopy } from '../AlphaIdCopy/AlphaIdCopy';
import './AlphaProperties.scss';

interface PowerPoolDescriptionTemplateMessageProps {
  onClickUseTemplate: (template: string) => void;
}

function PowerPoolDescriptionTemplateMessage({ onClickUseTemplate }: PowerPoolDescriptionTemplateMessageProps) {
  return (
    <Message size="tiny" info>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <FormattedMessageHelper tagName="h4" id="edit.properties.template.PowerPool.title" />
          <FormattedMessageHelper tagName="p" id="edit.properties.template.PowerPool.description" />
        </div>
        <Button onClick={() => onClickUseTemplate(POWER_POOL_DESCRIPTION_TEMPLATE)}>
          <FormattedMessageHelper id="edit.properties.template.PowerPool.use" />
        </Button>
      </div>
    </Message>
  );
}

const AlphaPropertiesDescription = ({
  superAlpha,
  regular,
  handleChange,
  handleOnBlur,
  selection,
  combo,
  alphaDetail,
  errorregular,
  errorselection,
  errorcombo,
  handleFocus,
  fieldOnFocus,
  haveResults,
  isAlphaCreatedByTheCurrentUser,
}: AlphaPropertiesDescriptionProps) => {
  // Users have to submit a Power Pool alpha with a description following the given template
  // We'll need to check if the alpha matches the competition, then show a message to the user to copy the template
  const isPowerPoolAlpha = React.useMemo(() => getIsPowerPoolAlpha(alphaDetail), [alphaDetail]);

  return (
    <React.Fragment>
      {!superAlpha && (
        <Grid.Column computer={16} mobile={16}>
          <FieldSet
            label="edit.properties.description"
            modifiers={['alpha-properties']}
            errors={errorregular}
            // Power Pool requires description to have at least 100 characters
            {...(isPowerPoolAlpha && {
              renderRightTitle: (
                <div className="form__label--restrition">
                  <FormattedMessageHelper
                    id="edit.properties.description.character.minimum"
                    values={{ charactersLength: regular ? regular.trim().length : 0 }}
                  />
                </div>
              ),
            })}
          >
            <>
              {isPowerPoolAlpha && (
                <PowerPoolDescriptionTemplateMessage
                  onClickUseTemplate={template => {
                    handleChange(null, { name: 'regular', value: template });
                  }}
                />
              )}
              <TextArea
                rows={3}
                placeholder="Description"
                className="form__textarea"
                name="regular"
                value={regular}
                onChange={handleChange}
                onBlur={handleOnBlur}
                onFocus={handleFocus}
                autoFocus={fieldOnFocus === 'regular' && haveResults}
                disabled={!isAlphaCreatedByTheCurrentUser}
              />
            </>
          </FieldSet>
        </Grid.Column>
      )}
      {superAlpha && (
        <React.Fragment>
          <Grid.Column computer={16} mobile={16}>
            <div className="alpha-properties__title">
              <FormattedMessageHelper id="edit.properties.description.label" />
            </div>
          </Grid.Column>
          <Grid.Column computer={16} mobile={16}>
            <FieldSet
              label="edit.properties.description.selection"
              modifiers={['alpha-properties']}
              errors={errorselection}
              renderRightTitle={
                <div className="form__label--restrition">
                  <FormattedMessageHelper
                    id="edit.properties.description.character.minimum"
                    values={{ charactersLength: selection ? selection.trim().length : 0 }}
                  />
                </div>
              }
            >
              <TextArea
                rows={3}
                autoFocus={fieldOnFocus === 'selection' && haveResults}
                placeholder="Notes"
                className="form__textarea"
                name="selection"
                value={selection}
                onChange={handleChange}
                onBlur={handleOnBlur}
                onFocus={handleFocus}
                disabled={!isAlphaCreatedByTheCurrentUser}
              />
            </FieldSet>
          </Grid.Column>
          <Grid.Column computer={16} mobile={16}>
            <FieldSet
              label="edit.properties.description.combo"
              modifiers={['alpha-properties']}
              errors={errorcombo}
              renderRightTitle={
                <div className="form__label--restrition">
                  <FormattedMessageHelper
                    id="edit.properties.description.character.minimum"
                    values={{ charactersLength: combo ? combo.trim().length : 0 }}
                  />
                </div>
              }
            >
              <TextArea
                rows={3}
                placeholder="Notes"
                className="form__textarea"
                name="combo"
                autoFocus={fieldOnFocus === 'combo' && haveResults}
                value={combo}
                onChange={handleChange}
                onBlur={handleOnBlur}
                onFocus={handleFocus}
                disabled={!isAlphaCreatedByTheCurrentUser}
              />
            </FieldSet>
          </Grid.Column>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export const AlphaPropertiesMain = ({
  handleChange,
  handleColorChange,
  tagsOptions,
  handleAddition,
  options,
  color,
  name,
  fieldOnFocus,
  haveResults,
  tags,
  category,
  regular,
  superAlpha,
  selection,
  combo,
  fetchingProperties,
  hasAlpha,
  alphaDetail,
  lastSavedDate,
  handleFocus,
  handleOnBlur,
  handleChangeDescriptions,
  errorcolor,
  errorname,
  errortags,
  errorcategory,
  errorregular,
  errorselection,
  errorcombo,
  isAlphaCreatedByTheCurrentUser,
  osmosisPointsOptions,
  osmosisPoints,
  errorosmosisPoints,
  isSubmitted,
  onCopyAlphaId,
}: AlphaPropertiesPropsMain) => (
  <React.Fragment>
    <div className="alphas_properties">
      <div>
        <Grid className="grid-account">
          <Grid.Column computer={16} mobile={16}>
            <header className="alphas_properties__header">
              <FormattedMessageHelper tagName="span" id="edit.properties.title" defaultMessage={localeData.en['edit.properties.title']} />
              <div className="alphas_properties__header--save">
                {!hasAlpha && !fetchingProperties && <FormattedMessageHelper id="edit.properties.simulate.to.save" />}
                {fetchingProperties && <FormattedMessageHelper id="edit.properties.saving.progress" />}
                {hasAlpha && !fetchingProperties && (
                  <FormattedMessageHelper id="edit.properties.last.saved" values={{ date: lastSavedDate }} />
                )}
              </div>
            </header>
          </Grid.Column>
          {alphaDetail && alphaDetail.get('id') && (
            <Grid.Column computer={16} mobile={16}>
              <AlphaIdCopy alphaId={alphaDetail.get('id')} className="alpha-properties__alpha-id" onCopy={onCopyAlphaId} />
            </Grid.Column>
          )}
          <Grid.Column computer={8} mobile={16}>
            <FieldSet label="edit.properties.name" modifiers={['alpha-properties']} errors={errorname}>
              <Input
                className="form__input"
                name="name"
                autoFocus={fieldOnFocus === 'name' && haveResults}
                placeholder="Currently 'anonymous'"
                value={name}
                onChange={handleChange}
                onBlur={handleOnBlur}
                onFocus={handleFocus}
                disabled={!isAlphaCreatedByTheCurrentUser}
              />
            </FieldSet>
          </Grid.Column>
          <Grid.Column computer={8} mobile={16}>
            <FieldSet label="edit.properties.category" modifiers={['alpha-properties']} errors={errorcategory}>
              {options && options !== null ? (
                <Dropdown
                  fluid={true}
                  className="dropdown-custom"
                  name="category"
                  placeholder="None"
                  selection={true}
                  value={category}
                  onChange={handleChange}
                  options={convertArrayFromDropdownProperties(options)}
                  onBlur={handleOnBlur}
                  onFocus={handleFocus}
                  disabled={!isAlphaCreatedByTheCurrentUser}
                />
              ) : (
                <div />
              )}
            </FieldSet>
          </Grid.Column>
          <Grid.Column computer={8} mobile={16}>
            <FieldSet label="edit.properties.tags" modifiers={['alpha-properties']} errors={errortags}>
              {tagsOptions && tagsOptions !== null ? (
                <Dropdown
                  options={tagsOptions}
                  name="tags"
                  placeholder="Select/add tags"
                  search={true}
                  selection={true}
                  className="dropdown-custom"
                  fluid={true}
                  multiple={true}
                  allowAdditions={true}
                  value={tags}
                  onAddItem={handleAddition}
                  onChange={handleChange}
                  onBlur={handleOnBlur}
                  onFocus={handleFocus}
                  disabled={!isAlphaCreatedByTheCurrentUser}
                />
              ) : (
                <div />
              )}
            </FieldSet>
          </Grid.Column>
          <Grid.Column computer={8} mobile={16}>
            <FieldSet label="edit.properties.color" modifiers={['alpha-properties']} errors={errorcolor}>
              <ColorProperties color={color} handleColorChange={handleColorChange} disabled={!isAlphaCreatedByTheCurrentUser} />
            </FieldSet>
          </Grid.Column>
          {osmosisPointsOptions && isSubmitted && (
            <Grid.Column computer={8} mobile={16}>
              <FieldSet label="edit.properties.osmosisPoints" modifiers={['alpha-properties']} errors={errorosmosisPoints}>
                <Input
                  className="form__input"
                  name="osmosisPoints"
                  type="number"
                  step="1"
                  autoFocus={fieldOnFocus === 'osmosisPoints' && haveResults}
                  placeholder="1–100000"
                  value={osmosisPoints !== undefined ? osmosisPoints : ''}
                  onChange={handleChange}
                  onBlur={handleOnBlur}
                  onFocus={handleFocus}
                  disabled={!isAlphaCreatedByTheCurrentUser}
                  min={1}
                  max={100000}
                />
              </FieldSet>
            </Grid.Column>
          )}
          <AlphaPropertiesDescription
            superAlpha={superAlpha}
            regular={regular}
            handleChange={handleChangeDescriptions}
            selection={selection}
            combo={combo}
            hasAlpha={hasAlpha}
            alphaDetail={alphaDetail}
            errorregular={errorregular}
            errorselection={errorselection}
            errorcombo={errorcombo}
            handleFocus={handleFocus}
            fieldOnFocus={fieldOnFocus}
            haveResults={haveResults}
            handleOnBlur={handleOnBlur}
            isAlphaCreatedByTheCurrentUser={isAlphaCreatedByTheCurrentUser}
          />
        </Grid>
      </div>
    </div>
  </React.Fragment>
);
